/**
 * SignaturePad is a class that enables users to draw signatures on an HTML canvas element.
 * It supports both mouse and touch input and provides methods to clear, load, and retrieve the signature as a base64 image.
 */
export class SignaturePad {
    private dotNetReference: any;
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private isDrawing: boolean = false;
    private lastX: number = 0;
    private lastY: number = 0;

    /**
     * Initializes a new instance of the SignaturePad.
     * @param canvas The HTMLCanvasElement to be used for drawing.
     */
    constructor(dotNetRef: any, canvas: HTMLCanvasElement) {
        this.dotNetReference = dotNetRef;
        this.canvas = canvas;

        const rect = this.canvas.getBoundingClientRect();
        this.canvas.width = rect.width;
        this.canvas.height = rect.height;

        this.ctx = this.canvas.getContext("2d") as CanvasRenderingContext2D;
        this.ctx.lineJoin = "round";
        this.ctx.lineCap = "round";

        this.AttachEventListeners();
    }

    /**
     * Clears the signature from the canvas.
     */
    public ClearSignature(): void {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    /**
     * Loads a pre-existing signature from a base64 image string.
     * @param base64Image The base64-encoded image string.
     */
    public LoadSignature(base64Image: string): void {
        const img = new Image();
        img.src = base64Image;
        img.onload = () => {
            this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
            this.ctx.drawImage(img, 0, 0, this.canvas.width, this.canvas.height);
        };
    }

    /**
     * Retrieves the current signature as a base64-encoded PNG image string.
     * @returns The base64 representation of the signature.
     */
    public GetSignature(): string {
        return this.canvas.toDataURL("image/png");
    }

    /**
     * Attaches event listeners for both mouse and touch input.
     */
    private AttachEventListeners(): void {
        // Mouse Events
        this.canvas.addEventListener("mousedown", (e) => this.StartDrawing(e));
        this.canvas.addEventListener("mousemove", (e) => this.Draw(e));
        this.canvas.addEventListener("mouseup", () => this.StopDrawing());
        this.canvas.addEventListener("mouseleave", () => this.StopDrawing());

        // Touch Events
        this.canvas.addEventListener("touchstart", (e) => this.HandleTouchStart(e));
        this.canvas.addEventListener("touchmove", (e) => this.HandleTouchMove(e));
        this.canvas.addEventListener("touchend", () => this.StopDrawing());
    }

    /**
     * Starts drawing when the user begins interaction.
     * @param event The mouse or touch event.
     */
    private StartDrawing(event: MouseEvent | TouchEvent): void {
        if (this.isDrawing == true) {
            return;
        }

        const { x, y } = this.GetEventCoordinates(event);
        this.isDrawing = true;
        this.lastX = x;
        this.lastY = y;
    }

    /**
     * Draws a smooth line from the previous point to the current point using quadratic curves.
     * @param event The mouse or touch event.
     */
    private Draw(event: MouseEvent | TouchEvent): void {
        if (this.isDrawing == false) {
            return;
        }

        const { x, y } = this.GetEventCoordinates(event);

        // Adjust line width dynamically based on movement speed
        const speed = Math.sqrt((x - this.lastX) ** 2 + (y - this.lastY) ** 2);
        this.ctx.lineWidth = Math.max(2, 5 - speed * 0.03);

        this.ctx.beginPath();
        this.ctx.moveTo(this.lastX, this.lastY);

        // Use quadratic curve for smooth transition
        const midX = (this.lastX + x) / 2;
        const midY = (this.lastY + y) / 2;
        this.ctx.quadraticCurveTo(this.lastX, this.lastY, midX, midY);
        this.ctx.stroke();

        // Update last position
        this.lastX = midX;
        this.lastY = midY;
    }

    /**
     * Stops drawing when the user releases the input.
     */
    private async StopDrawing(): Promise<void> {
        if (this.isDrawing == false) {
            return;
        }

        this.isDrawing = false;
        await this.dotNetReference.invokeMethodAsync("OnSignatureChangedInternalAsync");
    }

    /**
     * Handles touch start events by initiating drawing at the touched position.
     * @param event The touch event object.
     */
    private HandleTouchStart(event: TouchEvent): void {
        this.StartDrawing(event);
        event.preventDefault(); // Prevent scrolling
    }

    /**
     * Handles touch move events by drawing lines based on touch movement.
     * @param event The touch event object.
     */
    private HandleTouchMove(event: TouchEvent): void {
        this.Draw(event);
        event.preventDefault(); // Prevent scrolling
    }

    /**
     * Converts mouse or touch event coordinates to canvas-relative coordinates.
     * @param event The mouse or touch event.
     * @returns The adjusted canvas-relative coordinates.
     */
    private GetEventCoordinates(event: MouseEvent | TouchEvent): { x: number, y: number } {
        const rect = this.canvas.getBoundingClientRect();
        let clientX: number, clientY: number;

        if (event instanceof MouseEvent) {
            clientX = event.clientX;
            clientY = event.clientY;
        } else {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        }

        return {
            x: clientX - rect.left,
            y: clientY - rect.top
        };
    }
}

window['InitSignaturePad'] = function (dotnetRef, canvasRef) {
    return new SignaturePad(dotnetRef, canvasRef);
}